import { getCommandSpec } from '@tunasong/schemas'
import type { Rule } from '@tunasong/schemas'
import type { FC, ReactNode } from 'react'
import { useState } from 'react'
import { Alert } from '../primitives/alert.js'
import { Button } from '../primitives/button.js'
import { Chip } from '../primitives/chip.js'
import { Switch } from '../primitives/switch.js'
import { ConfirmDialog } from '../dialog/confirm-dialog.js'
import { CONVERT_ENTITY_TO_SONG_COMMAND_ID } from './automation-rules.js'
import { describeIncompleteness } from './rule-validation.js'
import { describeRule } from './rule-sentence.js'
import styles from './rule-card.module.css'

export interface RuleCardProps {
  rule: Rule
  /** When false the action's server handler / flag isn't live yet — enabling is gated (spec §3). */
  actionAvailable?: boolean
  /** Whether turning the rule on needs a confirm (irreversible action). Defaults from the convert command. */
  irreversible?: boolean
  /** When supplied, replaces the read-only sentence. Passed as a node so the card need not know the editor's props. */
  editor?: ReactNode
  /**
   * Disambiguates this card's switch and delete button from every other
   * card's on the same page, e.g. `"automation 2"`. Omit for single-rule
   * usage, where there is nothing to disambiguate.
   */
  ruleLabel?: string
  onToggle: (enabled: boolean) => void
  onTagChange?: (tag: string) => void
  onResume?: () => void
  onDelete?: () => void
  className?: string
}

export const RuleCard: FC<RuleCardProps> = props => {
  const { rule, actionAvailable = false, editor, ruleLabel, onToggle, onTagChange, onResume, onDelete, className } = props
  const qualify = (label: string) => (ruleLabel ? `${label} (${ruleLabel})` : label)
  const sentence = describeRule(rule)
  /*
   * `isReversible` on the command's registered spec is the general signal, so a
   * new irreversible action warns without this card learning its id. The convert
   * id remains a fallback for commands whose client-side spec has not registered
   * yet — dropping it would silently stop warning about the one action we ship.
   */
  const irreversible =
    props.irreversible ??
    rule.actions.some(action => {
      const spec = getCommandSpec(action.commandId)
      return spec ? spec.isReversible === false : action.commandId === CONVERT_ENTITY_TO_SONG_COMMAND_ID
    })
  const paused = rule.pausedReason === 'loop-guard'
  const incomplete = describeIncompleteness(rule)
  const blocked = !actionAvailable || Boolean(incomplete)

  /*
   * `undefined` when no confirm is pending; otherwise which control asked for
   * it, so the one shared dialog knows whether to call `onToggle(true)` or
   * `onResume()` on confirm. "Turn back on" is an enable, and `resumeRule`
   * writes `enabled: true` just like the switch does, so it must pass through
   * the same irreversible-action confirm rather than skip it.
   */
  const [pendingConfirm, setPendingConfirm] = useState<'toggle' | 'resume' | undefined>(undefined)
  const [confirmDelete, setConfirmDelete] = useState(false)
  const [editing, setEditing] = useState(false)
  const [draftTag, setDraftTag] = useState(sentence.tag ?? '')

  const actionLabel = actionAvailable ? sentence.actionLabel : 'Convert to Song — not available yet'
  const entityLabel = sentence.entityType ?? 'entity'
  const trigger = sentence.isFocus
    ? sentence.eventLabel
    : `When a ${entityLabel} is ${sentence.eventLabel} ${sentence.tag ?? ''}`.trim()
  const ariaLabel = `${trigger}, ${sentence.actionLabel}. Currently ${rule.enabled ? 'on' : 'off'}.`

  const handleToggle = (checked: boolean) => {
    if (checked && irreversible) {
      setPendingConfirm('toggle')
      return
    }
    onToggle(checked)
  }

  /*
   * Resuming a paused rule is an enable, so it must clear the same bar the
   * switch does: a rule that cannot be enabled (unavailable action,
   * unfinished condition) cannot be resumed either, and an irreversible
   * action asks for the same confirmation. `INBOX_ACTION_AVAILABLE = false`
   * currently keeps the switch permanently disabled, making this the only
   * enable path in the panel -- letting it skip the gates would make every
   * one of them decorative.
   */
  const handleResume = () => {
    if (blocked) {
      return
    }
    if (irreversible) {
      setPendingConfirm('resume')
      return
    }
    onResume?.()
  }

  const commitTag = () => {
    setEditing(false)
    const next = draftTag.trim()
    if (next && next !== sentence.tag) {
      onTagChange?.(next)
    }
  }

  const cardClass = [styles.card, className].filter(Boolean).join(' ')

  return (
    <div role="group" aria-label={ariaLabel} className={cardClass}>
      {editor ?? (
        <div className={styles.sentence}>
          {sentence.isFocus ? <strong>{sentence.eventLabel}</strong> : <span>When a note is</span>}
          {sentence.isFocus ? null : <strong>{sentence.eventLabel}</strong>}
          {sentence.tag !== undefined && !sentence.isFocus ? (
            editing ? (
              <input
                aria-label="Tag"
                className={styles.tagInput}
                value={draftTag}
                onChange={event => setDraftTag(event.target.value)}
                onKeyDown={event => {
                  if (event.key === 'Enter') {
                    commitTag()
                  }
                  if (event.key === 'Escape') {
                    setEditing(false)
                    setDraftTag(sentence.tag ?? '')
                  }
                }}
                onBlur={commitTag}
              />
            ) : (
              <>
                <Chip label={sentence.tag} />
                {onTagChange ? (
                  <Button
                    aria-label="Edit tag"
                    onClick={() => {
                      setDraftTag(sentence.tag ?? '')
                      setEditing(true)
                    }}
                  >
                    Edit
                  </Button>
                ) : null}
              </>
            )
          ) : null}
          <span aria-hidden="true">→</span>
          <strong>{actionLabel}</strong>
        </div>
      )}

      {irreversible ? <p className={styles.caption}>Converting can’t be undone.</p> : null}
      {incomplete ? <p className={styles.helper}>{incomplete}</p> : null}
      {!incomplete && !actionAvailable ? <p className={styles.helper}>This action is coming soon.</p> : null}

      {paused ? (
        <Alert
          severity="warning"
          aria-live="polite"
          action={
            <Button disabled={blocked} onClick={handleResume}>
              Turn back on
            </Button>
          }
        >
          <strong>Paused to prevent a loop.</strong> This rule tried to run too many times in a row, so we paused it.
          Turn it back on if this was expected.
        </Alert>
      ) : null}

      <div className={styles.controls}>
        {/*
         * `blocked` gates enabling only -- an already-enabled rule holding an
         * unrecognised action or a condition this client can no longer
         * validate must still be switchable off. Being unable to turn a
         * running automation off is worse than being unable to turn one on;
         * deleting it would be the only escape otherwise.
         */}
        <Switch
          checked={rule.enabled}
          disabled={!rule.enabled && blocked}
          onCheckedChange={handleToggle}
          aria-label={qualify(`Turn on: ${sentence.actionLabel}`)}
        />
        <span className={styles.state}>{rule.enabled ? 'On' : 'Off'}</span>
        {onDelete ? (
          <Button aria-label={qualify('Delete rule')} onClick={() => setConfirmDelete(true)}>
            Delete
          </Button>
        ) : null}
      </div>

      <ConfirmDialog
        open={pendingConfirm !== undefined}
        title="Turn on this rule?"
        text={`It will run “${sentence.actionLabel}” on matching ${entityLabel}s automatically. This can’t be undone.`}
        okText="Turn on"
        cancelText="Cancel"
        onConfirm={() => {
          const confirmed = pendingConfirm
          setPendingConfirm(undefined)
          if (confirmed === 'resume') {
            onResume?.()
            return
          }
          onToggle(true)
        }}
        onCancel={() => setPendingConfirm(undefined)}
      />
      <ConfirmDialog
        open={confirmDelete}
        title="Delete this rule?"
        text="It will stop running. You can set it up again later."
        okText="Delete"
        cancelText="Cancel"
        onConfirm={() => {
          setConfirmDelete(false)
          onDelete?.()
        }}
        onCancel={() => setConfirmDelete(false)}
      />
    </div>
  )
}
